import { useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import userApi from "../../api/user/user.api";
import { setAuthModalOpen } from "../../store/features/authModalSlice";
import { setUser } from "../../store/features/userSlice";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeClosed } from "lucide-react";

import ProcessingBar from "../../Util/Processingbar";
import { fadeUp } from "animations/variants";

const SigninForm = ({ switchAuthState, actionState }) => {
  const dispatch = useDispatch();
  const [isLoginRequest, setIsLoginRequest] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const signinForm = useFormik({
    initialValues: {
      password: "",
      username: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .min(3, "Username must be at least 3 characters")
        .required("Username is required"),
      password: Yup.string()
        .min(3, "Password must be at least 3 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values) => {
      setErrorMessage("");
      setIsLoginRequest(true);
      // let responseLogin = dispatch(login(values));
      const { response, err } = await userApi.signin(values);

      // console.log({ response });

      if (response) {
        signinForm.resetForm();
        setIsLoginRequest(false);
        dispatch(setUser(response.data));
        dispatch(setAuthModalOpen(false));
        toast.success("Sign in successful");
      }

      if (err) {
        setErrorMessage(err.message);
        setIsLoginRequest(false);
      }
    },
  });

  return (
    <motion.form
      onSubmit={signinForm.handleSubmit}
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <input
        type="text"
        name="username"
        placeholder="Email"
        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellowstar "
        value={signinForm.values.username}
        onChange={signinForm.handleChange}
      />
      {signinForm.touched.username && signinForm.errors.username && (
        <p className="text-red-500 text-sm">{signinForm.errors.username}</p>
      )}

      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          name="password"
          placeholder="Password"
          className="
      w-full px-4 py-2 pr-12
      border rounded-md
      focus:outline-none focus:ring-2 focus:ring-yellowstar
    "
          value={signinForm.values.password}
          onChange={signinForm.handleChange}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="
      absolute right-3 top-1/2 -translate-y-1/2
      text-gray-500 hover:text-yellowstar
      transition-colors
    "
        >
          {showPassword ? <EyeClosed size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {signinForm.touched.password && signinForm.errors.password && (
        <p className="text-red-500 text-sm">{signinForm.errors.password}</p>
      )}

      {signinForm.touched.password && signinForm.errors.password && (
        <p className="text-red-500 text-sm">{signinForm.errors.password}</p>
      )}

      <motion.button
        type="submit"
        whileTap={{ scale: 0.95 }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="w-full text-navy hover:text-white py-2 rounded-md transition duration-300 tracking-widest uppercase hover:shadow-lg"
        disabled={isLoginRequest}
        style={{
          background: "linear-gradient(-45deg, #fed693, #d09929)",
        }}
      >
        <AnimatePresence mode="wait">
          {isLoginRequest ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ProcessingBar />
            </motion.div>
          ) : (
            <motion.span
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              Sign In
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <button
        type="button"
        onClick={() => switchAuthState(actionState.forgot)}
        className="text-yellowstar text-sm hover:underline block text-center"
      >
        Forgot your password?
      </button>

      {/* Divider */}
      <motion.div
        variants={fadeUp}
        className="mt-6 flex items-center justify-center gap-4"
      >
        <div className="w-20 border-t border-dashed border-gray-300"></div>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
        <div className="w-20 border-t border-dashed border-gray-300"></div>
      </motion.div>

      {/* Sign Up Link */}
      <button
        type="button"
        onClick={() => switchAuthState("signup")}
        className="
    w-full mt-4 py-2
    tracking-widest uppercase
    border-b border-navy border-dotted
    text-navy
    transition-all duration-300
    hover:bg-gradient-to-r
    hover:from-[#d09929] hover:to-[#fed693]
    hover:text-white
    hover:border-transparent
    hover:shadow-lg
  "
      >
        Sign Up
      </button>

      {/* Error Message */}
      {errorMessage && (
        <p className="text-red-500 text-sm text-center">{errorMessage}</p>
      )}
    </motion.form>
  );
};

export default SigninForm;
